import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabaseClient } from '../services/supabase';
import { UserProfile, UserRole } from '../types/user';

interface UserRoleContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined);

export const useUserRole = () => {
  const context = useContext(UserRoleContext);
  if (context === undefined) {
    throw new Error('useUserRole must be used within a UserRoleProvider');
  }
  return context;
};

interface UserRoleProviderProps {
  children: React.ReactNode;
  userId: string | null | undefined;
  /** Optional auth user; used to create user_profiles row with role "user" when missing (new signups) */
  user?: { id: string; email?: string; user_metadata?: { full_name?: string; first_name?: string; last_name?: string } } | null;
}

export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({ children, userId, user }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setUserProfile(null);
      return;
    }

    try {
      const { data, error } = await supabaseClient
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // No profile yet (e.g. new signup): create one with role "user" so Super Admin must approve elevated roles
        const email = user?.email ?? '';
        const fullName = user?.user_metadata?.full_name ?? [user?.user_metadata?.first_name, user?.user_metadata?.last_name].filter(Boolean).join(' ') ?? '';
        const { error: insertError } = await supabaseClient
          .from('user_profiles')
          .upsert({
            id: userId,
            email,
            full_name: fullName,
            role: 'user',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, { onConflict: 'id' });

        if (insertError) {
          console.error('Error creating user profile:', insertError);
        }
        // Set in-memory profile as user (do not default to developer)
        const defaultProfile: UserProfile = {
          id: userId,
          email,
          role: 'user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setUserProfile(defaultProfile);
        if (!insertError) {
          // Refetch so next time we have DB row
          const { data: refetched } = await supabaseClient.from('user_profiles').select('*').eq('id', userId).single();
          if (refetched) setUserProfile(refetched);
        }
      } else {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      const defaultProfile: UserProfile = {
        id: userId,
        email: user?.email ?? '',
        role: 'user',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setUserProfile(defaultProfile);
    } finally {
      setLoading(false);
    }
  }, [userId, user?.email, user?.user_metadata?.full_name, user?.user_metadata?.first_name, user?.user_metadata?.last_name]);

  const refreshProfile = async () => {
    setLoading(true);
    await fetchUserProfile();
  };

  const hasRole = (role: UserRole): boolean => {
    return userProfile?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return userProfile ? roles.includes(userProfile.role) : false;
  };

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
    } else {
      // No user ID, set loading to false and userProfile to null
      setLoading(false);
      setUserProfile(null);
    }
  }, [userId, fetchUserProfile]);

  const value: UserRoleContextType = {
    userProfile,
    loading,
    refreshProfile,
    hasRole,
    hasAnyRole
  };

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
};
